import React from "react";
import { Modal, Descriptions, Badge, Tag } from "antd";
import styled from "styled-components";

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
  
  .ant-modal-header {
    background-color: #f8f9fa;
    border-radius: 8px 8px 0 0;
  }
  
  .ant-modal-title {
    color: #3c2f61;
    font-weight: 600;
  }
`;

const CreditCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 16px 0;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const CreditRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0;
  padding: 8px 0;
  border-bottom: ${props => props.isTotal ? '2px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.2)'};
  
  &:last-child {
    border-bottom: none;
    margin-top: 12px;
  }
`;

const CreditLabel = styled.span`
  font-size: ${props => props.isTotal ? '16px' : '14px'};
  font-weight: ${props => props.isTotal ? '600' : '400'};
  opacity: ${props => props.isTotal ? '1' : '0.9'};
`;

const CreditValue = styled.span`
  font-size: ${props => props.isTotal ? '18px' : '14px'};
  font-weight: ${props => props.isTotal ? 'bold' : '500'};
  color: ${props => props.isTotal ? '#fff' : 'rgba(255,255,255,0.95)'};
`;

const CurrencyTag = styled(Tag)`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-weight: 500;
  font-size: 12px;
  padding: 2px 8px;
`;

const SubscriptionCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
`;

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const options = { 
    year: "numeric", 
    month: "short", 
    day: "numeric"
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const formatCreditValue = (value, currency) => {
  if (!value && value !== 0) return "0";
  
  // Format number with commas
  const formattedNumber = value.toLocaleString();
  
  // Add currency if available
  if (currency) {
    return `${formattedNumber} ${currency}`;
  }
  
  return formattedNumber;
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "—";
  
  if (phoneNumber.startsWith('+')) {
    return phoneNumber;
  }
  
  return `+${phoneNumber}`;
};

const getStatusBadge = (status) => {
  const statusConfig = {
    'Active': { status: 'success', color: 'green' },
    'Inactive': { status: 'error', color: 'red' },
    'On Hold': { status: 'warning', color: 'orange' }
  };
  
  const config = statusConfig[status] || { status: 'default', color: 'gray' };
  return <Badge status={config.status} text={status} />;
};

const ParticipantDetailsModal = ({ 
  visible, 
  onCancel, 
  participant, 
  participantDetails 
}) => {
  if (!participant || !participantDetails) {
    return null;
  }

  const personalCredits = participantDetails.personal_credits || 0;
  const companyCredits = participantDetails.company_credits || 0;
  const totalCredits = personalCredits + companyCredits;
  const userCurrency = participantDetails.user_currency || null;

  const subscriptions = participantDetails.subscriptions || [];

  return (
    <StyledModal
      title={`${participant.employee_name} - Details`}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered
    >
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label="Full Name" span={2}>
          {participant.employee_name}
        </Descriptions.Item>
        
        <Descriptions.Item label="Phone Number" span={2}>
          {formatPhoneNumber(participant.user_phone)}
        </Descriptions.Item>
        
        <Descriptions.Item label="Email Address" span={2}>
          {participant.user_email || "—"}
        </Descriptions.Item>
        
        <Descriptions.Item label="Activities Participated">
          <Tag color="blue">{participant.activity_count} activities</Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label="Current Status">
          {getStatusBadge(participant.activity_status)}
        </Descriptions.Item>
        
        <Descriptions.Item label="Invited On">
          {participant.invited_on}
        </Descriptions.Item>
        
        <Descriptions.Item label="Joined On">
          {participant.joined_on}
        </Descriptions.Item>

       
      </Descriptions>

      {/* Credits Section */}
      <CreditCard style={{ position: 'relative' }}>
        
        
        <h4 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>Account Balance</h4>
        
        <CreditRow>
          <CreditLabel>Personal Balance:</CreditLabel>
          <CreditValue>{formatCreditValue(personalCredits, userCurrency)}</CreditValue>
        </CreditRow>
        
        <CreditRow>
          <CreditLabel>Company Balance:</CreditLabel>
          <CreditValue>{formatCreditValue(companyCredits, userCurrency)}</CreditValue>
        </CreditRow>
        
        <CreditRow isTotal>
          <CreditLabel isTotal>Total Balance:</CreditLabel>
          <CreditValue isTotal>{formatCreditValue(totalCredits, userCurrency)}</CreditValue>
        </CreditRow>
      </CreditCard>

      {/* Subscriptions Section */}
      <div style={{ marginTop: 24 }}>
        <h4 style={{ color: '#3c2f61', marginBottom: 16 }}>
          Subscriptions ({subscriptions.length})
        </h4>
        
        {subscriptions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>
            No active subscriptions found
          </div>
        ) : (
          subscriptions.map((subscription, index) => (
            <SubscriptionCard key={index}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <strong style={{ color: '#3c2f61' }}>
                    {subscription.provider_name}
                  </strong>
                  {subscription.subscription_name && (
                    <div style={{ fontSize: '14px', color: '#666', marginTop: 4 }}>
                      Plan: {subscription.subscription_name}
                    </div>
                  )}
                </div>
                {getStatusBadge(subscription.activity_status)}
              </div>
              
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Start Date">
                  {formatDate(subscription.subscription_start_date)}
                </Descriptions.Item>
                
                <Descriptions.Item label="Expiry Date">
                  {formatDate(subscription.subscription_expiry_date)}
                </Descriptions.Item>
                
                <Descriptions.Item label="Last Renewed">
                  {formatDate(subscription.subscription_last_renewed_at)}
                </Descriptions.Item>
                
                <Descriptions.Item label="Has Renewed">
                  <Tag color="purple">{subscription.subscription_renewal_count || 0} times</Tag>
                </Descriptions.Item>
              </Descriptions>
            </SubscriptionCard>
          ))
        )}
      </div>
    </StyledModal>
  );
};

export default ParticipantDetailsModal;